<script setup>
import TableList from '../others/TableList.vue'
import { ref, computed } from 'vue'
import SlideDetailView from '../others/SlideDetailView.vue'
import CouponForm from '../coupons/CouponForm.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import ListFetcher from '@/features/data/presentation/components/ListFetcher.vue'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'
import Commons from '@/shared/utils/commons'

const detailData = ref(null)
const editCoupon = ref(false)
const saveCouponTrigger = ref(null)
const restaurants = ref([])

const couponsCols = computed(() => [
  {
    name: 'Code & Titre',
    id: 'title',
    value(el) {
      const desc = el.description || '-'
      const truncatedDesc = desc.length > 50 ? desc.substring(0, 50) + '...' : desc
      return [el.code || '-', truncatedDesc]
    },
  },
  {
    name: 'Réduction',
    id: 'discount',
    value(el) {
      if (el.type === 'percentage') {
        return `<span class="font-semibold text-green-600">-${el.discount}%</span>`
      }
      return `<span class="font-semibold text-green-600">-${Commons.formatNumber(el.discount)} FCFA</span>`
    },
  },
  {
    name: 'Type',
    id: 'type',
    // filter: {
    //   key: 'type',
    //   default: '',
    //   options: [
    //     { name: 'Tout', value: '' },
    //     { name: 'Pourcentage', value: 'percentage' },
    //     { name: 'Montant fixe', value: 'fixed' },
    //   ],
    // },
    value(el) {
      const typeMap = {
        percentage: '<span class="text-blue-600 font-medium">Pourcentage</span>',
        fixed: '<span class="text-purple-600 font-medium">Montant fixe</span>',
      }
      return typeMap[el.type] || '-'
    },
  },
  {
    name: 'Restaurant',
    id: 'storeId',
    value(el) {
      return el.storeId === 'all'
        ? '<span class="text-orange-600 font-medium">Tous les restaurants</span>'
        : el.storeId || '-'
    },
  },
  {
    name: 'Validité',
    id: 'endDate',
    value(el) {
      const endDate = el.endDate ? new Date(el.endDate) : null
      const now = new Date()
      const isExpired = endDate && endDate < now

      if (isExpired) {
        return `<span class="text-red-600 font-medium">Expiré le ${Commons.time(endDate, 'DD MMM YYYY')}</span>`
      }
      return `Jusqu'au ${Commons.time(endDate, 'DD MMM YYYY')}`
    },
  },
  {
    name: 'Utilisations',
    id: 'nbAppliance',
    value(el) {
      const max = el.nbAppliance ? el.nbAppliance : '∞'
      return `${el.usageCount || 0} / ${max}`
    },
  },
  {
    name: 'Actions',
    id: 'action',
    icon: () => '/images/icons/dots.png',
    options: el => {
      const actions = []

      actions.push({
        name: 'Modifier',
        action: 'edit',
        icon: 'pi pi-pencil',
      })

      actions.push({
        name: 'Supprimer',
        action: 'delete',
        icon: 'pi pi-trash',
      })

      return actions
    },
    onClick(action, el) {
      if (action.action === 'edit') {
        detailData.value = { ...el, mode: 'edit' }
        editCoupon.value = true
      } else if (action.action === 'delete') {
        confirmDeleteCoupon(el)
      }
    },
    value() {},
  },
])

const buttons = [
  {
    title: '&plus; Nouveau Coupon',
    onClick() {
      detailData.value = { mode: 'create' }
      editCoupon.value = true
    },
  },
]

const confirmDeleteCoupon = async coupon => {
  const res = await window.customPrompt({
    title: `Voulez-vous vraiment supprimer le coupon "${coupon.code}" ?`,
    description: 'Cette action est irréversible.',
    formFields: [],
    successText: 'Supprimer',
    cancelText: 'Annuler',
  })

  if (res.accepted) {
    try {
      await saveCouponTrigger.value.trigger({
        action: 'delete',
        coupon: { id: coupon.id },
      })

      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Succès',
        body: 'Le coupon a été supprimé',
      })

      detailData.value = null
    } catch (error) {
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Erreur',
        body: 'Impossible de supprimer le coupon',
      })
    }
  }
}

const onCouponSaved = () => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: 'Le coupon a été enregistré',
  })
  editCoupon.value = false
  detailData.value = null
}

const onCouponError = error => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Erreur',
    body: `${error.result?.message || 'Erreur lors de l\'enregistrement'}`,
  })
}
</script>

<template>
  <div class="p-5 h-full overflow-auto bg-gray-50">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-800 mb-2">Gestion des Coupons</h1>
      <p class="text-gray-600">Créez et gérez les codes promos pour vos clients</p>
    </div>

    <!-- Coupons Table -->
    <TableList
      title="Coupons"
      dataKey="coupons"
      hasSearch
      :cols="couponsCols"
      :buttons="buttons"
      :refreshOnEvents="['COUPON_UPDATED']"
    ></TableList>

    <!-- Coupon Form Slide-over -->
    <SlideDetailView
      :isVisible="!!detailData && editCoupon"
      @hide="editCoupon = false; detailData = null"
    >
      <CouponForm
        v-if="detailData"
        :couponData="detailData"
        :availableRestaurants="restaurants"
        @saved="onCouponSaved"
        @cancel="editCoupon = false; detailData = null"
      />
    </SlideDetailView>
    <!-- Fetcher pour charger les restaurants (caché) -->
     <div class="hidden">
       <ListFetcher
         dataKey="restaurants"
         class="hidden"
         @fetched="({items})=>{
          restaurants = items
         }"
       />

     </div>

    <!-- Action Triggers -->
    <ActionTrigger
      dataKey="saveCoupon"
      ref="saveCouponTrigger"
      @success="onCouponSaved"
      @failed="onCouponError"
      dispatchOnSuccess="COUPON_UPDATED"
    />

  </div>
</template>
